import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';
import { logDebug } from '@/utils/errorHandler';

/**
 * Enterprise-grade hook for Product Detail Page translations with type safety and performance optimization
 * Provides centralized access to all ProductDetailPage-related translations
 */
export function useProductDetailPageTranslations() {
  // Use the default translation namespace since productdetailpage.json is merged into translation
  const { t, i18n } = useTranslation('translation');

  // One-time validation for critical translations (development only)
  if (typeof window !== 'undefined' && !(window as any).hasCheckedTranslations) {
    logDebug('Validating ProductDetailPage translations', {
      component: 'useProductDetailPageTranslations',
      assetsTitle: t('productdetailpage.tabs.productAssets.title'),
      attributesTitle: t('productdetailpage.tabs.productAttributes.panel.title'),
    });
    (window as any).hasCheckedTranslations = true;
  }

  // Helper functions for common translation patterns with type safety (memoized with useCallback)
  const getSidebarText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.sidebar.${key}`, params) || key;
    },
    [t]
  );

  const getPricingModalText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.pricingModal.${key}`, params) || key;
    },
    [t]
  );

  const getCategoryModalText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.categoryModal.${key}`, params) || key;
    },
    [t]
  );

  const getFamilyChangeModalText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.familyChangeModal.${key}`, params) || key;
    },
    [t]
  );

  const getTagsModalText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tagsModal.${key}`, params) || key;
    },
    [t]
  );

  const getPageText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.page.${key}`, params) || key;
    },
    [t]
  );

  const getDescriptionText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.description.${key}`, params) || key;
    },
    [t]
  );

  const getTabsText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.${key}`, params) || key;
    },
    [t]
  );

  const getAddPanelText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.actions.relatedProducts.addPanel.${key}`, params) || key;
    },
    [t]
  );

  const getAttributesText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.productAttributes.${key}`, params) || key;
    },
    [t]
  );

  const getAssetsText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.productAssets.${key}`, params) || key;
    },
    [t]
  );

  const getProductAssetsText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.productAssets.${key}`, params) || key;
    },
    [t]
  );

  const getCompletenessDrilldownText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.completenessDrilldown.${key}`, params) || key;
    },
    [t]
  );

  const getHistoryText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.history.${key}`, params) || key;
    },
    [t]
  );

  const getPriceTabText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.priceTab.${key}`, params) || key;
    },
    [t]
  );

  const getPricingFormText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.pricingForm.${key}`, params) || key;
    },
    [t]
  );

  const getCommentsText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.tabs.comments.${key}`, params) || key;
    },
    [t]
  );

  const getProductComponentsText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.productComponents.${key}`, params) || key;
    },
    [t]
  );

  const getLayoutText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.layout.${key}`, params) || key;
    },
    [t]
  );

  const getUIText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.ui.${key}`, params) || key;
    },
    [t]
  );

  const getPlatformSyncText = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      return t(`productdetailpage.platformSync.${key}`, params) || key;
    },
    [t]
  );

  // Safe translation wrapper with error handling
  const tSafe = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      try {
        return t(key, params) || key;
      } catch (error) {
        // Use the existing error logger instead of console
        logDebug(`Translation error for key: ${key}`, {
          error,
          component: 'useProductDetailPageTranslations',
        });
        return key;
      }
    },
    [t]
  );

  // Memoized page translations - needed by ProductDetail component
  const getPageLabels = useMemo(
    () => ({
      title: getPageText('title'),
      breadcrumb: {
        products: getPageText('breadcrumb.products'),
        productDetails: getPageText('breadcrumb.productDetails'),
      },
      header: {
        statusBadges: {
          active: getPageText('header.statusBadges.active'),
          inactive: getPageText('header.statusBadges.inactive'),
        },
        buttons: {
          printPdf: getPageText('header.buttons.printPdf'),
          archive: getPageText('header.buttons.archive'),
        },
        skuLabel: getPageText('header.skuLabel'),
      },
      loading: {
        skeleton: getPageText('loading.skeleton'),
      },
      errors: {
        productNotFound: getPageText('errors.productNotFound'),
        productIdMissing: getPageText('errors.productIdMissing'),
        emptyData: getPageText('errors.emptyData'),
        loadFailed: getPageText('errors.loadFailed'),
        genericError: getPageText('errors.genericError'),
        backToProducts: getPageText('errors.backToProducts'),
        tryAgain: getPageText('errors.tryAgain'),
      },
      archiveModal: {
        title: getPageText('archiveModal.title'),
        description: getPageText('archiveModal.description'),
        confirmText: getPageText('archiveModal.confirmText'),
        cancelText: getPageText('archiveModal.cancelText'),
        warningText: getPageText('archiveModal.warningText'),
        metadata: {
          sku: getPageText('archiveModal.metadata.sku'),
          name: getPageText('archiveModal.metadata.name'),
          status: getPageText('archiveModal.metadata.status'),
        },
      },
    }),
    [getPageText]
  );

  // Memoized sidebar translations - needed by sidebar components
  const getSidebarLabels = useMemo(
    () => ({
      sections: {
        coreInformation: getSidebarText('sections.coreInformation'),
        category: getSidebarText('sections.category'),
        family: getSidebarText('sections.family'),
        additionalInformation: getSidebarText('sections.additionalInformation'),
        tags: getSidebarText('sections.tags'),
        pricing: getSidebarText('sections.pricing'),
        status: getSidebarText('sections.status'),
        created: getSidebarText('sections.created'),
        lastModified: getSidebarText('sections.lastModified'),
      },
      fields: {
        sku: getSidebarText('fields.sku'),
        name: getSidebarText('fields.name'),
        brand: getSidebarText('fields.brand'),
        gtin: getSidebarText('fields.gtin'),
      },
      placeholders: {
        enterBrandName: getSidebarText('placeholders.enterBrandName'),
        enterGtinBarcode: getSidebarText('placeholders.enterGtinBarcode'),
      },
      buttons: {
        uploadImage: getSidebarText('buttons.uploadImage'),
        change: getSidebarText('buttons.change'),
        manage: getSidebarText('buttons.manage'),
        viewAllPrices: getSidebarText('buttons.viewAllPrices'),
      },
      states: {
        uncategorized: getSidebarText('states.uncategorized'),
        noFamilyAssigned: getSidebarText('states.noFamilyAssigned'),
        noTags: getSidebarText('states.noTags'),
        noPriceSet: getSidebarText('states.noPriceSet'),
        loadingPrices: getSidebarText('states.loadingPrices'),
        uploading: getSidebarText('states.uploading'),
        moreItems: getSidebarText('states.moreItems'),
      },
      tooltips: {
        viewProductImage: getSidebarText('tooltips.viewProductImage'),
        unknownUser: getSidebarText('tooltips.unknownUser'),
        relativeTimeByUser: getSidebarText('tooltips.relativeTimeByUser'),
      },
      validation: {
        skuRequired: getSidebarText('validation.skuRequired'),
        nameRequired: getSidebarText('validation.nameRequired'),
        gtinFormat: getSidebarText('validation.gtinFormat'),
      },
      meta: {
        unknown: getSidebarText('meta.unknown'),
      },
    }),
    [getSidebarText]
  );

  // Memoized tabs translations - needed by ProductDetailTabs component
  const getTabsLabels = useMemo(
    () => ({
      labels: {
        overview: getTabsText('labels.overview'),
        attributes: getTabsText('labels.attributes'),
        assets: getTabsText('labels.assets'),
        history: getTabsText('labels.history'),
        price: getTabsText('labels.price'),
        comments: getTabsText('labels.comments'),
      },
      dataCompleteness: {
        title: getTabsText('dataCompleteness.title'),
        description: getTabsText('dataCompleteness.description'),
        overallCompleteness: getTabsText('dataCompleteness.overallCompleteness'),
        missingInformation: getTabsText('dataCompleteness.missingInformation'),
        fieldStatusBreakdown: getTabsText('dataCompleteness.fieldStatusBreakdown'),
        fieldsComplete: getTabsText('dataCompleteness.fieldsComplete'),
        viewAllFieldStatuses: getTabsText('dataCompleteness.viewAllFieldStatuses'),
        noFieldDetails: getTabsText('dataCompleteness.noFieldDetails'),
        fieldNames: {
          name: getTabsText('dataCompleteness.fieldNames.name'),
          sku: getTabsText('dataCompleteness.fieldNames.sku'),
          description: getTabsText('dataCompleteness.fieldNames.description'),
          category: getTabsText('dataCompleteness.fieldNames.category'),
          brand: getTabsText('dataCompleteness.fieldNames.brand'),
          gtinBarcode: getTabsText('dataCompleteness.fieldNames.gtinBarcode'),
          tags: getTabsText('dataCompleteness.fieldNames.tags'),
          images: getTabsText('dataCompleteness.fieldNames.images'),
        },
        ariaLabels: {
          expandCollapse: {
            expand: getTabsText('dataCompleteness.ariaLabels.expandCollapse.expand'),
            collapse: getTabsText('dataCompleteness.ariaLabels.expandCollapse.collapse'),
          },
          completenessProgress: getTabsText('dataCompleteness.ariaLabels.completenessProgress'),
          criticalGaps: getTabsText('dataCompleteness.ariaLabels.criticalGaps'),
        },
        categories: {
          critical: getTabsText('dataCompleteness.categories.critical'),
          recommended: getTabsText('dataCompleteness.categories.recommended'),
          optional: getTabsText('dataCompleteness.categories.optional'),
        },
        statusSummary: {
          complete: getTabsText('dataCompleteness.statusSummary.complete'),
          incomplete: getTabsText('dataCompleteness.statusSummary.incomplete'),
        },
        states: {
          unavailable: getTabsText('dataCompleteness.states.unavailable'),
          moreDots: getTabsText('dataCompleteness.states.moreDots'),
          viewAll: getTabsText('dataCompleteness.states.viewAll'),
        },
      },
      media: {
        title: getTabsText('media.title'),
        description: getTabsText('media.description'),
        primaryBadge: getTabsText('media.primaryBadge'),
        noImagesAvailable: getTabsText('media.noImagesAvailable'),
        addImages: getTabsText('media.addImages'),
        viewAllImages: getTabsText('media.viewAllImages'),
        imageAlt: getTabsText('media.imageAlt'),
        errorPlaceholder: getTabsText('media.errorPlaceholder'),
      },
      loading: {
        assets: getTabsText('loading.assets'),
        completeness: getTabsText('loading.completeness'),
      },
      actions: {
        relatedProducts: {
          title: getTabsText('actions.relatedProducts.title'),
          loadingDescription: getTabsText('actions.relatedProducts.loadingDescription'),
          emptyState: {
            title: getTabsText('actions.relatedProducts.emptyState.title'),
            description: getTabsText('actions.relatedProducts.emptyState.description'),
          },
          pagination: {
            page: getTabsText('actions.relatedProducts.pagination.page'),
            previousPage: getTabsText('actions.relatedProducts.pagination.previousPage'),
            nextPage: getTabsText('actions.relatedProducts.pagination.nextPage'),
          },
          actions: {
            tryAgain: getTabsText('actions.relatedProducts.actions.tryAgain'),
            refresh: getTabsText('actions.relatedProducts.actions.refresh'),
          },
          description: getTabsText('actions.relatedProducts.description'),
          relationshipTypes: {
            accessory: getTabsText('actions.relatedProducts.relationshipTypes.accessory'),
            variant: getTabsText('actions.relatedProducts.relationshipTypes.variant'),
            frequently_bought_together: getTabsText(
              'actions.relatedProducts.relationshipTypes.frequently_bought_together'
            ),
            replacement: getTabsText('actions.relatedProducts.relationshipTypes.replacement'),
            similar: getTabsText('actions.relatedProducts.relationshipTypes.similar'),
            general: getTabsText('actions.relatedProducts.relationshipTypes.general'),
            related: getTabsText('actions.relatedProducts.relationshipTypes.related'),
          },
          relationshipDescriptions: {
            accessory: getTabsText('actions.relatedProducts.relationshipDescriptions.accessory'),
            variant: getTabsText('actions.relatedProducts.relationshipDescriptions.variant'),
            frequently_bought_together: getTabsText(
              'actions.relatedProducts.relationshipDescriptions.frequently_bought_together'
            ),
            replacement: getTabsText(
              'actions.relatedProducts.relationshipDescriptions.replacement'
            ),
            similar: getTabsText('actions.relatedProducts.relationshipDescriptions.similar'),
            general: getTabsText('actions.relatedProducts.relationshipDescriptions.general'),
          },
          sourceTypes: {
            manual: getTabsText('actions.relatedProducts.sourceTypes.manual'),
            suggested: getTabsText('actions.relatedProducts.sourceTypes.suggested'),
            categoryMatch: getTabsText('actions.relatedProducts.sourceTypes.categoryMatch'),
          },
          tooltips: {
            manuallyAdded: getTabsText('actions.relatedProducts.tooltips.manuallyAdded'),
            algorithmicSuggestion: getTabsText(
              'actions.relatedProducts.tooltips.algorithmicSuggestion'
            ),
            categoryMatch: getTabsText('actions.relatedProducts.tooltips.categoryMatch'),
            pinned: getTabsText('actions.relatedProducts.tooltips.pinned'),
            unpinProduct: getTabsText('actions.relatedProducts.tooltips.unpinProduct'),
            pinProduct: getTabsText('actions.relatedProducts.tooltips.pinProduct'),
            editRelationship: getTabsText('actions.relatedProducts.tooltips.editRelationship'),
            removeProduct: getTabsText('actions.relatedProducts.tooltips.removeProduct'),
          },
          dialog: {
            title: getTabsText('actions.relatedProducts.dialog.title'),
            description: getTabsText('actions.relatedProducts.dialog.description'),
            labels: {
              relationshipType: getTabsText(
                'actions.relatedProducts.dialog.labels.relationshipType'
              ),
              notes: getTabsText('actions.relatedProducts.dialog.labels.notes'),
            },
            placeholders: {
              selectRelationshipType: getTabsText(
                'actions.relatedProducts.dialog.placeholders.selectRelationshipType'
              ),
              addNotes: getTabsText('actions.relatedProducts.dialog.placeholders.addNotes'),
            },
            helpText: {
              notes: getTabsText('actions.relatedProducts.dialog.helpText.notes'),
            },
            selectOptions: {
              accessory: getTabsText('actions.relatedProducts.dialog.selectOptions.accessory'),
              variant: getTabsText('actions.relatedProducts.dialog.selectOptions.variant'),
              frequently_bought_together: getTabsText(
                'actions.relatedProducts.dialog.selectOptions.frequently_bought_together'
              ),
              replacement: getTabsText('actions.relatedProducts.dialog.selectOptions.replacement'),
              similar: getTabsText('actions.relatedProducts.dialog.selectOptions.similar'),
              general: getTabsText('actions.relatedProducts.dialog.selectOptions.general'),
            },
            buttons: {
              cancel: getTabsText('actions.relatedProducts.dialog.buttons.cancel'),
              saveChanges: getTabsText('actions.relatedProducts.dialog.buttons.saveChanges'),
              saving: getTabsText('actions.relatedProducts.dialog.buttons.saving'),
            },
          },
          notifications: {
            relationshipUpdated: getTabsText(
              'actions.relatedProducts.notifications.relationshipUpdated'
            ),
            relationshipUpdateFailed: getTabsText(
              'actions.relatedProducts.notifications.relationshipUpdateFailed'
            ),
            relationshipAdded: getTabsText(
              'actions.relatedProducts.notifications.relationshipAdded'
            ),
            relationshipRemoved: getTabsText(
              'actions.relatedProducts.notifications.relationshipRemoved'
            ),
            relationshipRemoveFailed: getTabsText(
              'actions.relatedProducts.notifications.relationshipRemoveFailed'
            ),
            productPinned: getTabsText('actions.relatedProducts.notifications.productPinned'),
            productUnpinned: getTabsText('actions.relatedProducts.notifications.productUnpinned'),
            pinFailed: getTabsText('actions.relatedProducts.notifications.pinFailed'),
            unpinFailed: getTabsText('actions.relatedProducts.notifications.unpinFailed'),
            addRelatedSuccess: getTabsText(
              'actions.relatedProducts.notifications.addRelatedSuccess'
            ),
            addRelatedFailed: getTabsText('actions.relatedProducts.notifications.addRelatedFailed'),
            addMultipleFailed: getTabsText(
              'actions.relatedProducts.notifications.addMultipleFailed'
            ),
            invalidProductRemoval: getTabsText(
              'actions.relatedProducts.notifications.invalidProductRemoval'
            ),
            cannotPinProduct: getTabsText('actions.relatedProducts.notifications.cannotPinProduct'),
          },
          aria: {
            pinUnpinProduct: getTabsText('actions.relatedProducts.aria.pinUnpinProduct'),
            editRelationship: getTabsText('actions.relatedProducts.aria.editRelationship'),
            removeProduct: getTabsText('actions.relatedProducts.aria.removeProduct'),
            pinnedLabel: getTabsText('actions.relatedProducts.aria.pinnedLabel'),
          },
          states: {
            unknownDate: getTabsText('actions.relatedProducts.states.unknownDate'),
            noImage: getTabsText('actions.relatedProducts.states.noImage'),
          },
          panel: {
            errorLoading: getTabsText('actions.relatedProducts.panel.errorLoading'),
            unknownProduct: getTabsText('actions.relatedProducts.panel.unknownProduct'),
            saving: getTabsText('actions.relatedProducts.panel.saving'),
            saveChanges: getTabsText('actions.relatedProducts.panel.saveChanges'),
            categoryMatch: getTabsText('actions.relatedProducts.panel.categoryMatch'),
            noRelationDescription: getTabsText(
              'actions.relatedProducts.panel.noRelationDescription'
            ),
            sourceBadges: {
              manual: getTabsText('actions.relatedProducts.panel.sourceBadges.manual'),
              algorithm: getTabsText('actions.relatedProducts.panel.sourceBadges.algorithm'),
              implicit: getTabsText('actions.relatedProducts.panel.sourceBadges.implicit'),
            },
            actions: {
              pin: getTabsText('actions.relatedProducts.panel.actions.pin'),
              unpin: getTabsText('actions.relatedProducts.panel.actions.unpin'),
              pinTooltip: getTabsText('actions.relatedProducts.panel.actions.pinTooltip'),
              unpinTooltip: getTabsText('actions.relatedProducts.panel.actions.unpinTooltip'),
              editRelationship: getTabsText(
                'actions.relatedProducts.panel.actions.editRelationship'
              ),
              removeProduct: getTabsText('actions.relatedProducts.panel.actions.removeProduct'),
            },
          },
          addPanel: {
            title: getTabsText('actions.relatedProducts.addPanel.title'),
            tooltip: getTabsText('actions.relatedProducts.addPanel.tooltip'),
            form: {
              labels: {
                relationshipType: getTabsText(
                  'actions.relatedProducts.addPanel.form.labels.relationshipType'
                ),
                notes: getTabsText('actions.relatedProducts.addPanel.form.labels.notes'),
                search: getTabsText('actions.relatedProducts.addPanel.form.labels.search'),
              },
              placeholders: {
                relationshipType: getTabsText(
                  'actions.relatedProducts.addPanel.form.placeholders.relationshipType'
                ),
                notes: getTabsText('actions.relatedProducts.addPanel.form.placeholders.notes'),
                search: getTabsText('actions.relatedProducts.addPanel.form.placeholders.search'),
              },
              buttons: {
                addNotes: getTabsText('actions.relatedProducts.addPanel.form.buttons.addNotes'),
                addSelected: getTabsText(
                  'actions.relatedProducts.addPanel.form.buttons.addSelected'
                ),
              },
              aria: {
                searchProducts: getTabsText(
                  'actions.relatedProducts.addPanel.form.aria.searchProducts'
                ),
                addSelectedProducts: getTabsText(
                  'actions.relatedProducts.addPanel.form.aria.addSelectedProducts'
                ),
                addSuggestedProduct: getTabsText(
                  'actions.relatedProducts.addPanel.form.aria.addSuggestedProduct'
                ),
              },
            },
            suggestions: {
              title: getTabsText('actions.relatedProducts.addPanel.suggestions.title'),
              description: getTabsText('actions.relatedProducts.addPanel.suggestions.description'),
              loading: getTabsText('actions.relatedProducts.addPanel.suggestions.loading'),
            },
            results: {
              noResults: getTabsText('actions.relatedProducts.addPanel.results.noResults'),
              productCard: {
                sku: getTabsText('actions.relatedProducts.addPanel.results.productCard.sku'),
                price: getTabsText('actions.relatedProducts.addPanel.results.productCard.price'),
              },
            },
            notes: {
              help: getTabsText('actions.relatedProducts.addPanel.notes.help'),
            },
            notifications: {
              searchFailed: getTabsText(
                'actions.relatedProducts.addPanel.notifications.searchFailed'
              ),
              suggestedProductAdded: getTabsText(
                'actions.relatedProducts.addPanel.notifications.suggestedProductAdded'
              ),
              suggestedProductAddFailed: getTabsText(
                'actions.relatedProducts.addPanel.notifications.suggestedProductAddFailed'
              ),
              multipleProductsAdded: getTabsText(
                'actions.relatedProducts.addPanel.notifications.multipleProductsAdded'
              ),
              multipleProductsAddFailed: getTabsText(
                'actions.relatedProducts.addPanel.notifications.multipleProductsAddFailed'
              ),
            },
          },
        },
      },
    }),
    [getTabsText]
  );

  // Memoized description translations - needed by ProductDetailDescription component
  const getDescriptionLabels = useMemo(
    () => ({
      title: getDescriptionText('title'),
      edit: {
        button: getDescriptionText('edit.button'),
        buttonAriaLabel: getDescriptionText('edit.buttonAriaLabel'),
        placeholder: getDescriptionText('edit.placeholder'),
        characterCount: getDescriptionText('edit.characterCount'),
        characterOverLimit: getDescriptionText('edit.characterOverLimit'),
        ariaLabel: getDescriptionText('edit.ariaLabel'),
        ariaRequired: getDescriptionText('edit.ariaRequired'),
        save: getDescriptionText('edit.save'),
        cancel: getDescriptionText('edit.cancel'),
        saving: getDescriptionText('edit.saving'),
        saveAriaLabel: getDescriptionText('edit.saveAriaLabel'),
        cancelAriaLabel: getDescriptionText('edit.cancelAriaLabel'),
      },
      states: {
        lastEditedBy: getDescriptionText('states.lastEditedBy'),
        unknownEditor: getDescriptionText('states.unknownEditor'),
      },
      empty: {
        title: getDescriptionText('empty.title'),
        subtitle: getDescriptionText('empty.subtitle'),
        addButton: getDescriptionText('empty.addButton'),
        addButtonAriaLabel: getDescriptionText('empty.addButtonAriaLabel'),
      },
      validation: {
        maxLengthExceeded: getDescriptionText('validation.maxLengthExceeded'),
      },
      errors: {
        saveError: getDescriptionText('errors.saveError'),
      },
    }),
    [getDescriptionText]
  );

  // Memoized tags modal translations - needed by ProductTagsModal component
  const getTagsModalLabels = useMemo(
    () => ({
      title: getTagsModalText('title'),
      description: getTagsModalText('description'),
      labels: {
        selectedTags: getTagsModalText('labels.selectedTags'),
        addTags: getTagsModalText('labels.addTags'),
        searchOrCreate: getTagsModalText('labels.searchOrCreate'),
      },
      placeholders: {
        searchTags: getTagsModalText('placeholders.searchTags'),
      },
      buttons: {
        save: getTagsModalText('buttons.save'),
        cancel: getTagsModalText('buttons.cancel'),
        saving: getTagsModalText('buttons.saving'),
      },
      states: {
        noTags: getTagsModalText('states.noTags'),
        loadingTags: getTagsModalText('states.loadingTags'),
      },
      notifications: {
        tagCreated: getTagsModalText('notifications.tagCreated'),
        tagCreateFailed: getTagsModalText('notifications.tagCreateFailed'),
        tagsUpdated: getTagsModalText('notifications.tagsUpdated'),
        updateFailed: getTagsModalText('notifications.updateFailed'),
        loadFailed: getTagsModalText('notifications.loadFailed'),
      },
    }),
    [getTagsModalText]
  );

  // Memoized attributes translations - needed by ProductAttributesPanel and EffectiveAttributesView components
  const getAttributesLabels = useMemo(
    () => ({
      panel: {
        title: getAttributesText('panel.title'),
        loading: getAttributesText('panel.loading'),
        noGroups: getAttributesText('panel.noGroups'),
        emptyState: {
          title: getAttributesText('panel.emptyState.title'),
          description: getAttributesText('panel.emptyState.description'),
          createMode: getAttributesText('panel.emptyState.createMode'),
        },
        validation: {
          email: getAttributesText('panel.validation.email'),
          phone: getAttributesText('panel.validation.phone'),
          url: getAttributesText('panel.validation.url'),
          date: getAttributesText('panel.validation.date'),
        },
        formatHints: {
          phone: getAttributesText('panel.formatHints.phone'),
          email: getAttributesText('panel.formatHints.email'),
          url: getAttributesText('panel.formatHints.url'),
          date: getAttributesText('panel.formatHints.date'),
        },
        media: {
          uploading: getAttributesText('panel.media.uploading'),
          assetId: getAttributesText('panel.media.assetId'),
          remove: getAttributesText('panel.media.remove'),
        },
        tableHeaders: {
          name: getAttributesText('panel.tableHeaders.name'),
          type: getAttributesText('panel.tableHeaders.type'),
          locale: getAttributesText('panel.tableHeaders.locale'),
          channel: getAttributesText('panel.tableHeaders.channel'),
          value: getAttributesText('panel.tableHeaders.value'),
          actions: getAttributesText('panel.tableHeaders.actions'),
        },
        localeChannel: {
          allLocales: getAttributesText('panel.localeChannel.allLocales'),
          allChannels: getAttributesText('panel.localeChannel.allChannels'),
          notApplicable: getAttributesText('panel.localeChannel.notApplicable'),
        },
        tooltips: {
          editAttribute: getAttributesText('panel.tooltips.editAttribute'),
          deleteAttribute: getAttributesText('panel.tooltips.deleteAttribute'),
          saveAttribute: getAttributesText('panel.tooltips.saveAttribute'),
          cancelEdit: getAttributesText('panel.tooltips.cancelEdit'),
        },
        deleteConfirmation: {
          title: getAttributesText('panel.deleteConfirmation.title'),
          message: getAttributesText('panel.deleteConfirmation.message'),
        },
      },
      groups: {
        ungrouped: getAttributesText('groups.ungrouped'),
        addTooltip: getAttributesText('groups.addTooltip'),
        addSuccess: getAttributesText('groups.addSuccess'),
        addError: getAttributesText('groups.addError'),
        allGroupsAdded: getAttributesText('groups.allGroupsAdded'),
        deleteGroup: getAttributesText('groups.deleteGroup'),
        addGroup: getAttributesText('groups.addGroup'),
      },
      buttons: {
        addAttribute: getAttributesText('buttons.addAttribute'),
        deleteGroup: getAttributesText('buttons.deleteGroup'),
        save: getAttributesText('buttons.save'),
        cancel: getAttributesText('buttons.cancel'),
        edit: getAttributesText('buttons.edit'),
        delete: getAttributesText('buttons.delete'),
      },
      addAttributeModal: {
        title: getAttributesText('addAttributeModal.title'),
        description: getAttributesText('addAttributeModal.description'),
        selectAttribute: getAttributesText('addAttributeModal.selectAttribute'),
        buttons: {
          add: getAttributesText('addAttributeModal.buttons.add'),
          cancel: getAttributesText('addAttributeModal.buttons.cancel'),
        },
        success: getAttributesText('addAttributeModal.success'),
        error: getAttributesText('addAttributeModal.error'),
      },
      deleteGroupModal: {
        title: getAttributesText('deleteGroupModal.title'),
        warning: getAttributesText('deleteGroupModal.warning'),
        selectLabel: getAttributesText('deleteGroupModal.selectLabel'),
        selectPlaceholder: getAttributesText('deleteGroupModal.selectPlaceholder'),
        aboutToDelete: getAttributesText('deleteGroupModal.aboutToDelete'),
        allAttributesIn: getAttributesText('deleteGroupModal.allAttributesIn'),
        attributesIn: getAttributesText('deleteGroupModal.attributesIn'),
        actionDescription: getAttributesText('deleteGroupModal.actionDescription'),
        buttons: {
          confirm: getAttributesText('deleteGroupModal.buttons.confirm'),
          deleting: getAttributesText('deleteGroupModal.buttons.deleting'),
          cancel: getAttributesText('deleteGroupModal.buttons.cancel'),
        },
      },
      localeChannelSelector: {
        localeLabel: getAttributesText('localeChannelSelector.localeLabel'),
        channelLabel: getAttributesText('localeChannelSelector.channelLabel'),
        localeChangeTooltip: getAttributesText('localeChannelSelector.localeChangeTooltip'),
        channelChangeTooltip: getAttributesText('localeChannelSelector.channelChangeTooltip'),
      },
      effectiveView: {
        title: getAttributesText('effectiveView.title'),
        description: getAttributesText('effectiveView.description'),
        loading: getAttributesText('effectiveView.loading'),
        error: getAttributesText('effectiveView.error'),
        noData: getAttributesText('effectiveView.noData'),
        inherited: getAttributesText('effectiveView.inherited'),
        overridden: getAttributesText('effectiveView.overridden'),
        local: getAttributesText('effectiveView.local'),
        family: getAttributesText('effectiveView.family'),
        fallback: getAttributesText('effectiveView.fallback'),
        showInherited: getAttributesText('effectiveView.showInherited'),
        hideInherited: getAttributesText('effectiveView.hideInherited'),
      },
      badges: {
        required: getAttributesText('badges.required'),
      },
      labels: {
        attributeCount: getAttributesText('labels.attributeCount'),
        attributeCountPlural: getAttributesText('labels.attributeCount_plural'),
      },
      dataTypes: {
        text: getAttributesText('dataTypes.text'),
        textarea: getAttributesText('dataTypes.textarea'),
        rich_text: getAttributesText('dataTypes.rich_text'),
        number: getAttributesText('dataTypes.number'),
        email: getAttributesText('dataTypes.email'),
        phone: getAttributesText('dataTypes.phone'),
        url: getAttributesText('dataTypes.url'),
        date: getAttributesText('dataTypes.date'),
        boolean: getAttributesText('dataTypes.boolean'),
        price: getAttributesText('dataTypes.price'),
        measurement: getAttributesText('dataTypes.measurement'),
        file: getAttributesText('dataTypes.file'),
        select: getAttributesText('dataTypes.select'),
        multiselect: getAttributesText('dataTypes.multiselect'),
      },
      placeholders: {
        textInput: getAttributesText('placeholders.textInput'),
        numberInput: getAttributesText('placeholders.numberInput'),
        emailInput: getAttributesText('placeholders.emailInput'),
        phoneInput: getAttributesText('placeholders.phoneInput'),
        urlInput: getAttributesText('placeholders.urlInput'),
        selectOption: getAttributesText('placeholders.selectOption'),
        amount: getAttributesText('placeholders.amount'),
        currency: getAttributesText('placeholders.currency'),
        unit: getAttributesText('placeholders.unit'),
      },
      errors: {
        saveError: getAttributesText('errors.saveError'),
        loadError: getAttributesText('errors.loadError'),
        deleteError: getAttributesText('errors.deleteError'),
        uploadError: getAttributesText('errors.uploadError'),
        invalidFile: getAttributesText('errors.invalidFile'),
      },
      notifications: {
        attributeAdded: getAttributesText('notifications.attributeAdded'),
        attributeUpdated: getAttributesText('notifications.attributeUpdated'),
        attributeDeleted: getAttributesText('notifications.attributeDeleted'),
        groupDeleted: getAttributesText('notifications.groupDeleted'),
        addFailed: getAttributesText('notifications.addFailed'),
        updateFailed: getAttributesText('notifications.updateFailed'),
        deleteFailed: getAttributesText('notifications.deleteFailed'),
      },
    }),
    [getAttributesText]
  );

  // Memoized assets translations - needed by AssetCard and AssetsTab components
  const getAssetsLabels = useMemo(
    () => ({
      title: getAssetsText('title'),
      loading: getAssetsText('loading'),
      noAssetsFound: getAssetsText('noAssetsFound'),
      addAsset: getAssetsText('addAsset'),
      upload: {
        dragDrop: getAssetsText('upload.dragDrop'),
        description: getAssetsText('upload.description'),
        selectFiles: getAssetsText('upload.selectFiles'),
        uploading: getAssetsText('upload.uploading'),
        uploadProgress: getAssetsText('upload.uploadProgress'),
        uploadComplete: getAssetsText('upload.uploadComplete'),
        uploadFailed: getAssetsText('upload.uploadFailed'),
        uploadError: getAssetsText('upload.uploadError'),
        retry: getAssetsText('upload.retry'),
        cancel: getAssetsText('upload.cancel'),
        maxSize: getAssetsText('upload.maxSize'),
      },
      filters: {
        title: getAssetsText('filters.title'),
        expandFilters: getAssetsText('filters.expandFilters'),
        collapseFilters: getAssetsText('filters.collapseFilters'),
        type: getAssetsText('filters.type'),
        uploadDate: getAssetsText('filters.uploadDate'),
        search: getAssetsText('filters.search'),
        tags: getAssetsText('filters.tags'),
        dateRange: getAssetsText('filters.dateRange'),
        nameSearch: getAssetsText('filters.nameSearch'),
        searchPlaceholder: getAssetsText('filters.searchPlaceholder'),
        reset: getAssetsText('filters.reset'),
        noTagsFound: getAssetsText('filters.noTagsFound'),
        activeFilter: getAssetsText('filters.activeFilter'),
      },
      bulkActions: {
        selectAll: getAssetsText('bulkActions.selectAll'),
        deselectAll: getAssetsText('bulkActions.deselectAll'),
        selected: getAssetsText('bulkActions.selected'),
        downloadZip: getAssetsText('bulkActions.downloadZip'),
        createBundle: getAssetsText('bulkActions.createBundle'),
        archiveSelected: getAssetsText('bulkActions.archiveSelected'),
      },
      assetCard: {
        unnamed: getAssetsText('assetCard.unnamed'),
        unnamedAsset: getAssetsText('assetCard.unnamedAsset'),
        unknown: getAssetsText('assetCard.unknown'),
        unknownDate: getAssetsText('assetCard.unknownDate'),
        imageError: getAssetsText('assetCard.imageError'),
        addTags: getAssetsText('assetCard.addTags'),
        addNewTag: getAssetsText('assetCard.addNewTag'),
        addTagButton: getAssetsText('assetCard.addTagButton'),
        noTags: getAssetsText('assetCard.noTags'),
        sizeLabel: getAssetsText('assetCard.sizeLabel'),
        primaryBadge: getAssetsText('assetCard.primaryBadge'),
        setAsPrimary: getAssetsText('assetCard.setAsPrimary'),
        assetMenu: getAssetsText('assetCard.assetMenu'),
        selectAsset: getAssetsText('assetCard.selectAsset'),
        deselectAsset: getAssetsText('assetCard.deselectAsset'),
        buttons: {
          save: getAssetsText('assetCard.buttons.save'),
          cancel: getAssetsText('assetCard.buttons.cancel'),
          editTags: getAssetsText('assetCard.buttons.editTags'),
          download: getAssetsText('assetCard.buttons.download'),
          rename: getAssetsText('assetCard.buttons.rename'),
          archive: getAssetsText('assetCard.buttons.archive'),
        },
      },
      renameDialog: {
        title: getAssetsText('renameDialog.title'),
        description: getAssetsText('renameDialog.description'),
        nameLabel: getAssetsText('renameDialog.nameLabel'),
        buttons: {
          save: getAssetsText('renameDialog.buttons.save'),
          cancel: getAssetsText('renameDialog.buttons.cancel'),
          saving: getAssetsText('renameDialog.buttons.saving'),
        },
      },
      bundle: {
        createTitle: getAssetsText('bundle.createTitle'),
        createDescription: getAssetsText('bundle.createDescription'),
        nameLabel: getAssetsText('bundle.nameLabel'),
        namePlaceholder: getAssetsText('bundle.namePlaceholder'),
        selectedAssets: getAssetsText('bundle.selectedAssets'),
        noAssetsSelected: getAssetsText('bundle.noAssetsSelected'),
        buttons: {
          create: getAssetsText('bundle.buttons.create'),
          cancel: getAssetsText('bundle.buttons.cancel'),
          creating: getAssetsText('bundle.buttons.creating'),
        },
      },
      bulkDownload: {
        title: getAssetsText('bulkDownload.title'),
        buttonText: getAssetsText('bulkDownload.buttonText'),
        ariaLabel: getAssetsText('bulkDownload.ariaLabel'),
        downloadZip: getAssetsText('bulkDownload.downloadZip'),
        downloading: getAssetsText('bulkDownload.downloading'),
        preparingDownload: getAssetsText('bulkDownload.preparingDownload'),
        downloadReady: getAssetsText('bulkDownload.downloadReady'),
        downloadFailed: getAssetsText('bulkDownload.downloadFailed'),
      },
      emptyStates: {
        noAssets: getAssetsText('emptyStates.noAssets'),
        noAssetsDescription: getAssetsText('emptyStates.noAssetsDescription'),
        uploadFirst: getAssetsText('emptyStates.uploadFirst'),
        noSearchResults: getAssetsText('emptyStates.noSearchResults'),
        noSearchResultsDescription: getAssetsText('emptyStates.noSearchResultsDescription'),
        clearFilters: getAssetsText('emptyStates.clearFilters'),
      },
      fileTypes: {
        image: getAssetsText('fileTypes.image'),
        document: getAssetsText('fileTypes.document'),
        spreadsheet: getAssetsText('fileTypes.spreadsheet'),
        pdf: getAssetsText('fileTypes.pdf'),
        video: getAssetsText('fileTypes.video'),
        audio: getAssetsText('fileTypes.audio'),
        archive: getAssetsText('fileTypes.archive'),
        other: getAssetsText('fileTypes.other'),
      },
      notifications: {
        assetUploaded: getAssetsText('notifications.assetUploaded'),
        uploadFailed: getAssetsText('notifications.uploadFailed'),
        primarySet: getAssetsText('notifications.primarySet'),
        primarySetFailed: getAssetsText('notifications.primarySetFailed'),
        assetDeleted: getAssetsText('notifications.assetDeleted'),
        deleteFailed: getAssetsText('notifications.deleteFailed'),
        tagsUpdated: getAssetsText('notifications.tagsUpdated'),
        tagsUpdateFailed: getAssetsText('notifications.tagsUpdateFailed'),
        assetRenamed: getAssetsText('notifications.assetRenamed'),
        renameFailed: getAssetsText('notifications.renameFailed'),
        bundleCreated: getAssetsText('notifications.bundleCreated'),
        bundleCreateFailed: getAssetsText('notifications.bundleCreateFailed'),
        downloadStarted: getAssetsText('notifications.downloadStarted'),
        downloadFailed: getAssetsText('notifications.downloadFailed'),
      },
      errors: {
        loadFailed: getAssetsText('errors.loadFailed'),
        uploadFailed: getAssetsText('errors.uploadFailed'),
        fileTooLarge: getAssetsText('errors.fileTooLarge'),
        invalidFileType: getAssetsText('errors.invalidFileType'),
        networkError: getAssetsText('errors.networkError'),
        serverError: getAssetsText('errors.serverError'),
      },
    }),
    [getAssetsText]
  );

  // Memoized history translations - needed by ProductHistoryTab component
  const getHistoryLabels = useMemo(
    () => ({
      title: getHistoryText('title'),
      loading: getHistoryText('loading'),
      filters: {
        changeType: getHistoryText('filters.changeType'),
        dateRange: getHistoryText('filters.dateRange'),
        changedBy: getHistoryText('filters.changedBy'),
        selectChangeType: getHistoryText('filters.selectChangeType'),
        selectUser: getHistoryText('filters.selectUser'),
        resetFilters: getHistoryText('filters.resetFilters'),
        all: getHistoryText('filters.all'),
      },
      eventTypes: {
        created: getHistoryText('eventTypes.created'),
        updated: getHistoryText('eventTypes.updated'),
        bulkUpdated: getHistoryText('eventTypes.bulkUpdated'),
        priceChanged: getHistoryText('eventTypes.priceChanged'),
        familyChanged: getHistoryText('eventTypes.familyChanged'),
        stockChanged: getHistoryText('eventTypes.stockChanged'),
        categoryChanged: getHistoryText('eventTypes.categoryChanged'),
        descriptionChanged: getHistoryText('eventTypes.descriptionChanged'),
        assetAdded: getHistoryText('eventTypes.assetAdded'),
        assetRemoved: getHistoryText('eventTypes.assetRemoved'),
        activated: getHistoryText('eventTypes.activated'),
        deactivated: getHistoryText('eventTypes.deactivated'),
        deleted: getHistoryText('eventTypes.deleted'),
        bulkDeleted: getHistoryText('eventTypes.bulkDeleted'),
        priceCreated: getHistoryText('eventTypes.priceCreated'),
        priceUpdated: getHistoryText('eventTypes.priceUpdated'),
        priceDeleted: getHistoryText('eventTypes.priceDeleted'),
        archived: getHistoryText('eventTypes.archived'),
        rollback: getHistoryText('eventTypes.rollback'),
      },
      fieldTypes: {
        status: getHistoryText('fieldTypes.status'),
        family: getHistoryText('fieldTypes.family'),
        price: getHistoryText('fieldTypes.price'),
        name: getHistoryText('fieldTypes.name'),
        sku: getHistoryText('fieldTypes.sku'),
        description: getHistoryText('fieldTypes.description'),
        category: getHistoryText('fieldTypes.category'),
        brand: getHistoryText('fieldTypes.brand'),
        gtin: getHistoryText('fieldTypes.gtin'),
        tags: getHistoryText('fieldTypes.tags'),
        images: getHistoryText('fieldTypes.images'),
        created_by: getHistoryText('fieldTypes.created_by'),
        is_archived: getHistoryText('fieldTypes.is_archived'),
        organization: getHistoryText('fieldTypes.organization'),
        barcode: getHistoryText('fieldTypes.barcode'),
      },
      values: {
        none: getHistoryText('values.none'),
        empty: getHistoryText('values.empty'),
        yes: getHistoryText('values.yes'),
        no: getHistoryText('values.no'),
        unknown: getHistoryText('values.unknown'),
      },
      eventCard: {
        by: getHistoryText('eventCard.by'),
        system: getHistoryText('eventCard.system'),
        unknownUser: getHistoryText('eventCard.unknownUser'),
      },
      changeDetails: {
        previousState: getHistoryText('changeDetails.previousState'),
        bulkOperation: getHistoryText('changeDetails.bulkOperation'),
        fieldUpdated: getHistoryText('changeDetails.fieldUpdated'),
        newValue: getHistoryText('changeDetails.newValue'),
        operation: getHistoryText('changeDetails.operation'),
        bulkUpdate: getHistoryText('changeDetails.bulkUpdate'),
        assetInformation: getHistoryText('changeDetails.assetInformation'),
        eventDetails: getHistoryText('changeDetails.eventDetails'),
        noAdditionalDetails: getHistoryText('changeDetails.noAdditionalDetails'),
      },
      emptyState: {
        title: getHistoryText('emptyState.title'),
        description: getHistoryText('emptyState.description'),
        resetFiltersLink: getHistoryText('emptyState.resetFiltersLink'),
        resetFiltersLinkEnd: getHistoryText('emptyState.resetFiltersLinkEnd'),
      },
      dateLabels: {
        today: getHistoryText('dateLabels.today'),
        yesterday: getHistoryText('dateLabels.yesterday'),
      },
      pagination: {
        showMoreAttributes: getHistoryText('pagination.showMoreAttributes'),
        viewMoreDays: getHistoryText('pagination.viewMoreDays'),
        loadMoreFromServer: getHistoryText('pagination.loadMoreFromServer'),
        loadingMore: getHistoryText('pagination.loadingMore'),
      },
      notifications: {
        loadFailed: getHistoryText('notifications.loadFailed'),
        loadFailedDescription: getHistoryText('notifications.loadFailedDescription'),
        resetFiltersFailed: getHistoryText('notifications.resetFiltersFailed'),
        resetFiltersFailedDescription: getHistoryText(
          'notifications.resetFiltersFailedDescription'
        ),
      },
      datePicker: {
        pickDateRange: getHistoryText('datePicker.pickDateRange'),
        selectEndDate: getHistoryText('datePicker.selectEndDate'),
        selectDateRange: getHistoryText('datePicker.selectDateRange'),
        fromDate: getHistoryText('datePicker.fromDate'),
        toDate: getHistoryText('datePicker.toDate'),
        rangeSelected: getHistoryText('datePicker.rangeSelected'),
        selectStartDate: getHistoryText('datePicker.selectStartDate'),
        clear: getHistoryText('datePicker.clear'),
      },
      errors: {
        eventNotFound: getHistoryText('errors.eventNotFound'),
        missingFieldParameter: getHistoryText('errors.missingFieldParameter'),
        eventNoValidPayload: getHistoryText('errors.eventNoValidPayload'),
        noChangesFoundForField: getHistoryText('errors.noChangesFoundForField'),
        noPreviousValueFound: getHistoryText('errors.noPreviousValueFound'),
        productNotFound: getHistoryText('errors.productNotFound'),
        noBasePriceFound: getHistoryText('errors.noBasePriceFound'),
        fieldAlreadyHasValue: getHistoryText('errors.fieldAlreadyHasValue'),
        categoryNotFound: getHistoryText('errors.categoryNotFound'),
        invalidCategoryValue: getHistoryText('errors.invalidCategoryValue'),
        noBasePriceRecord: getHistoryText('errors.noBasePriceRecord'),
        failedToRollback: getHistoryText('errors.failedToRollback'),
      },
      summaries: {
        productCreated: getHistoryText('summaries.productCreated'),
        productUpdated: getHistoryText('summaries.productUpdated'),
        productDeleted: getHistoryText('summaries.productDeleted'),
        productArchived: getHistoryText('summaries.productArchived'),
        productBulkDeleted: getHistoryText('summaries.productBulkDeleted'),
        tagsBulkUpdated: getHistoryText('summaries.tagsBulkUpdated'),
        productBulkUpdated: getHistoryText('summaries.productBulkUpdated'),
        productBulkStatusUpdated: getHistoryText('summaries.productBulkStatusUpdated'),
        priceAdded: getHistoryText('summaries.priceAdded'),
        priceUpdated: getHistoryText('summaries.priceUpdated'),
        priceDeleted: getHistoryText('summaries.priceDeleted'),
        assetRemoved: getHistoryText('summaries.assetRemoved'),
        rolledBackPrice: getHistoryText('summaries.rolledBackPrice'),
        rolledBackCategory: getHistoryText('summaries.rolledBackCategory'),
        rolledBackField: getHistoryText('summaries.rolledBackField'),
        rolledBackPriceField: getHistoryText('summaries.rolledBackPriceField'),
        rolledBackGeneric: getHistoryText('summaries.rolledBackGeneric'),
      },
    }),
    [getHistoryText]
  );

  // Memoized price tab translations - needed by PriceTab component
  const getPriceTabLabels = useMemo(
    () => ({
      title: getPriceTabText('title'),
      summary: {
        title: getPriceTabText('summary.title'),
        minPrice: getPriceTabText('summary.minPrice'),
        avgPrice: getPriceTabText('summary.avgPrice'),
        maxPrice: getPriceTabText('summary.maxPrice'),
        totalPrices: getPriceTabText('summary.totalPrices'),
        noData: getPriceTabText('summary.noData'),
      },
      actions: {
        addPrice: getPriceTabText('actions.addPrice'),
        addFirstPrice: getPriceTabText('actions.addFirstPrice'),
        editPrice: getPriceTabText('actions.editPrice'),
        deletePrice: getPriceTabText('actions.deletePrice'),
        deleteSelected: getPriceTabText('actions.deleteSelected'),
        tryAgain: getPriceTabText('actions.tryAgain'),
      },
      filters: {
        searchPlaceholder: getPriceTabText('filters.searchPlaceholder'),
        allCurrencies: getPriceTabText('filters.allCurrencies'),
        selectCurrency: getPriceTabText('filters.selectCurrency'),
        validToPlaceholder: getPriceTabText('filters.validToPlaceholder'),
      },
      table: {
        headers: {
          priceType: getPriceTabText('table.headers.priceType'),
          currency: getPriceTabText('table.headers.currency'),
          value: getPriceTabText('table.headers.value'),
          validFrom: getPriceTabText('table.headers.validFrom'),
          validTo: getPriceTabText('table.headers.validTo'),
          actions: getPriceTabText('table.headers.actions'),
        },
        selectAll: getPriceTabText('table.selectAll'),
        selectPrice: getPriceTabText('table.selectPrice'),
        noData: getPriceTabText('table.noData'),
        selected: getPriceTabText('table.selected'),
        prices: getPriceTabText('table.prices'),
        expired: getPriceTabText('table.expired'),
      },
      drawer: {
        addTitle: getPriceTabText('drawer.addTitle'),
        editTitle: getPriceTabText('drawer.editTitle'),
        addDescription: getPriceTabText('drawer.addDescription'),
        editDescription: getPriceTabText('drawer.editDescription'),
        cancel: getPriceTabText('drawer.cancel'),
      },
      deleteDialog: {
        title: getPriceTabText('deleteDialog.title'),
        description: getPriceTabText('deleteDialog.description'),
        cancel: getPriceTabText('deleteDialog.cancel'),
        confirm: getPriceTabText('deleteDialog.confirm'),
      },
      loading: {
        summary: getPriceTabText('loading.summary'),
        prices: getPriceTabText('loading.prices'),
      },
      errors: {
        loadFailed: getPriceTabText('errors.loadFailed'),
        deleteFailed: getPriceTabText('errors.deleteFailed'),
        deleteMultipleFailed: getPriceTabText('errors.deleteMultipleFailed'),
        loadSummaryFailed: getPriceTabText('errors.loadSummaryFailed'),
      },
    }),
    [getPriceTabText]
  );

  // Memoized pricing form translations - needed by PricingForm component
  const getPricingFormLabels = useMemo(
    () => ({
      labels: {
        priceType: getPricingFormText('labels.priceType'),
        currency: getPricingFormText('labels.currency'),
        priceValue: getPricingFormText('labels.priceValue'),
        validFrom: getPricingFormText('labels.validFrom'),
        validTo: getPricingFormText('labels.validTo'),
      },
      placeholders: {
        selectPriceType: getPricingFormText('placeholders.selectPriceType'),
        selectCurrency: getPricingFormText('placeholders.selectCurrency'),
        enterValue: getPricingFormText('placeholders.enterValue'),
        neverExpires: getPricingFormText('placeholders.neverExpires'),
      },
      buttons: {
        addPrice: getPricingFormText('buttons.addPrice'),
        updatePrice: getPricingFormText('buttons.updatePrice'),
      },
      validation: {
        priceTypeRequired: getPricingFormText('validation.priceTypeRequired'),
        currencyRequired: getPricingFormText('validation.currencyRequired'),
        valueRequired: getPricingFormText('validation.valueRequired'),
      },
      loading: {
        metadata: getPricingFormText('loading.metadata'),
        saving: getPricingFormText('loading.saving'),
      },
      errors: {
        loadMetadata: getPricingFormText('errors.loadMetadata'),
        loadMetadataDesc: getPricingFormText('errors.loadMetadataDesc'),
        saveFailed: getPricingFormText('errors.saveFailed'),
      },
      notifications: {
        success: getPricingFormText('notifications.success'),
        error: getPricingFormText('notifications.error'),
        priceAdded: getPricingFormText('notifications.priceAdded'),
        priceUpdated: getPricingFormText('notifications.priceUpdated'),
      },
    }),
    [getPricingFormText]
  );

  // Notification helper functions with parameter support
  const getSidebarNotification = (key: string, params?: Record<string, unknown>): string => {
    return getSidebarText(`notifications.${key}`, params);
  };

  const getPricingModalNotification = (key: string, params?: Record<string, unknown>): string => {
    return getPricingModalText(`notifications.${key}`, params);
  };

  const getCategoryModalNotification = (key: string, params?: Record<string, unknown>): string => {
    return getCategoryModalText(`messages.${key}`, params);
  };

  const getFamilyChangeModalNotification = (
    key: string,
    params?: Record<string, unknown>
  ): string => {
    return getFamilyChangeModalText(`notifications.${key}`, params);
  };

  const getTagsModalNotification = (key: string, params?: Record<string, unknown>): string => {
    return getTagsModalText(`notifications.${key}`, params);
  };

  const getPageNotification = (key: string, params?: Record<string, unknown>): string => {
    return getPageText(`notifications.${key}`, params);
  };

  const getDescriptionNotification = (key: string, params?: Record<string, unknown>): string => {
    return getDescriptionText(`notifications.${key}`, params);
  };

  const getTabsNotification = (key: string, params?: Record<string, unknown>): string => {
    return getTabsText(`notifications.${key}`, params);
  };

  const getAddPanelNotification = (key: string, params?: Record<string, unknown>): string => {
    return getAddPanelText(`notifications.${key}`, params);
  };

  const getAttributesNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.tabs.productAttributes.notifications.${key}`, params || {}));
  };

  const getAssetsNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.tabs.productAssets.notifications.${key}`, params || {}));
  };

  const getHistoryNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.tabs.history.notifications.${key}`, params || {}));
  };

  const getPriceTabNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.priceTab.notifications.${key}`, params || {}));
  };

  const getPricingFormNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.pricingForm.notifications.${key}`, params || {}));
  };

  const getCommentsNotification = (key: string, params?: Record<string, unknown>): string => {
    return String(t(`productdetailpage.tabs.comments.${key}`, params || {}));
  };

  // Memoized pricing modal translations - needed by PricingModal component
  const getPricingModalLabels = useMemo(
    () => ({
      title: getPricingModalText('title'),
      description: getPricingModalText('description'),
      tabs: {
        current: getPricingModalText('tabs.current'),
        add: getPricingModalText('tabs.add'),
      },
      table: {
        headers: {
          priceType: getPricingModalText('table.headers.priceType'),
          amount: getPricingModalText('table.headers.amount'),
          currency: getPricingModalText('table.headers.currency'),
          channel: getPricingModalText('table.headers.channel'),
          lastUpdated: getPricingModalText('table.headers.lastUpdated'),
          actions: getPricingModalText('table.headers.actions'),
        },
        noData: getPricingModalText('table.noData'),
        loadingPrices: getPricingModalText('table.loadingPrices'),
        loadingMetadata: getPricingModalText('table.loadingMetadata'),
      },
      form: {
        labels: {
          priceType: getPricingModalText('form.labels.priceType'),
          amount: getPricingModalText('form.labels.amount'),
          currency: getPricingModalText('form.labels.currency'),
          channel: getPricingModalText('form.labels.channel'),
          validFrom: getPricingModalText('form.labels.validFrom'),
          validTo: getPricingModalText('form.labels.validTo'),
        },
        placeholders: {
          selectPriceType: getPricingModalText('form.placeholders.selectPriceType'),
          enterAmount: getPricingModalText('form.placeholders.enterAmount'),
          selectCurrency: getPricingModalText('form.placeholders.selectCurrency'),
          selectChannel: getPricingModalText('form.placeholders.selectChannel'),
        },
        buttons: {
          addPrice: getPricingModalText('form.buttons.addPrice'),
          updatePrice: getPricingModalText('form.buttons.updatePrice'),
          cancel: getPricingModalText('form.buttons.cancel'),
        },
      },
      buttons: {
        close: getPricingModalText('buttons.close'),
        edit: getPricingModalText('buttons.edit'),
        delete: getPricingModalText('buttons.delete'),
      },
      validation: {
        priceTypeRequired: getPricingModalText('validation.priceTypeRequired'),
        amountRequired: getPricingModalText('validation.amountRequired'),
        amountInvalid: getPricingModalText('validation.amountInvalid'),
        currencyRequired: getPricingModalText('validation.currencyRequired'),
      },
    }),
    [getPricingModalText]
  );

  // Memoized comment tab translations - needed by ProductCommentsTab component
  const getCommentsTabLabels = useMemo(
    () => ({
      title: getCommentsText('tab.title'),
      header: {
        searchPlaceholder: getCommentsText('tab.header.searchPlaceholder'),
      },
      actions: {
        addComment: getCommentsText('tab.actions.addComment'),
        addFirstComment: getCommentsText('tab.actions.addFirstComment'),
        expandAll: getCommentsText('tab.actions.expandAll'),
        collapseAll: getCommentsText('tab.actions.collapseAll'),
        newest: getCommentsText('tab.actions.newest'),
        oldest: getCommentsText('tab.actions.oldest'),
      },
      emptyState: {
        noComments: getCommentsText('tab.emptyState.noComments'),
        beFirstToComment: getCommentsText('tab.emptyState.beFirstToComment'),
        noSearchResults: getCommentsText('tab.emptyState.noSearchResults'),
      },
      form: {
        addTitle: getCommentsText('tab.form.addTitle'),
      },
      errors: {
        loadFailed: getCommentsText('tab.errors.loadFailed'),
      },
    }),
    [getCommentsText]
  );

  // Memoized comment form translations - needed by CommentForm component
  const getCommentsFormLabels = useMemo(
    () => ({
      placeholders: {
        writeComment: getCommentsText('form.placeholders.writeComment'),
      },
      actions: {
        post: getCommentsText('form.actions.post'),
        update: getCommentsText('form.actions.update'),
        cancel: getCommentsText('form.actions.cancel'),
        posting: getCommentsText('form.actions.posting'),
        updating: getCommentsText('form.actions.updating'),
      },
      hints: {
        submitShortcut: getCommentsText('form.hints.submitShortcut'),
      },
      validation: {
        commentEmpty: getCommentsText('form.validation.commentEmpty'),
        commentTooLong: getCommentsText('form.validation.commentTooLong'),
      },
      errors: {
        submitFailed: getCommentsText('form.errors.submitFailed'),
      },
    }),
    [getCommentsText]
  );

  // Memoized comment item translations - needed by CommentItem component
  const getCommentsItemLabels = useMemo(
    () => ({
      badges: {
        reply: getCommentsText('item.badges.reply'),
        edited: getCommentsText('item.badges.edited'),
      },
      actions: {
        reply: getCommentsText('item.actions.reply'),
        edit: getCommentsText('item.actions.edit'),
        delete: getCommentsText('item.actions.delete'),
      },
      tooltips: {
        threadDepth: getCommentsText('item.tooltips.threadDepth'),
      },
      deleteDialog: {
        title: getCommentsText('item.deleteDialog.title'),
        description: getCommentsText('item.deleteDialog.description'),
        actions: {
          cancel: getCommentsText('item.deleteDialog.actions.cancel'),
          delete: getCommentsText('item.deleteDialog.actions.delete'),
          deleting: getCommentsText('item.deleteDialog.actions.deleting'),
        },
      },
    }),
    [getCommentsText]
  );

  // Memoized comment stats translations - needed by CommentStats component
  const getCommentsStatsLabels = useMemo(
    () => ({
      cards: {
        totalComments: {
          label: getCommentsText('stats.cards.totalComments.label'),
          description: getCommentsText('stats.cards.totalComments.description'),
        },
        discussions: {
          label: getCommentsText('stats.cards.discussions.label'),
          description: getCommentsText('stats.cards.discussions.description'),
        },
        replies: {
          label: getCommentsText('stats.cards.replies.label'),
        },
        recentActivity: {
          label: getCommentsText('stats.cards.recentActivity.label'),
          description: getCommentsText('stats.cards.recentActivity.description'),
          badge: getCommentsText('stats.cards.recentActivity.badge'),
        },
      },
      contributors: {
        title: getCommentsText('stats.contributors.title'),
      },
      engagement: {
        title: getCommentsText('stats.engagement.title'),
        discussionRate: {
          label: getCommentsText('stats.engagement.discussionRate.label'),
        },
        activityLevel: {
          label: getCommentsText('stats.engagement.activityLevel.label'),
        },
      },
    }),
    [getCommentsText]
  );

  // Memoized category modal translations - needed by CategoryModal component
  const getCategoryModalLabels = useMemo(
    () => ({
      title: getCategoryModalText('title'),
      description: getCategoryModalText('description'),
      tabs: {
        assign: getCategoryModalText('tabs.assign'),
        create: getCategoryModalText('tabs.create'),
        edit: getCategoryModalText('tabs.edit'),
      },
      search: {
        label: getCategoryModalText('search.label'),
        placeholder: getCategoryModalText('search.placeholder'),
      },
      loading: {
        categories: getCategoryModalText('loading.categories'),
        updating: getCategoryModalText('loading.updating'),
        creating: getCategoryModalText('loading.creating'),
        deleting: getCategoryModalText('loading.deleting'),
      },
      error: {
        title: getCategoryModalText('error.title'),
        loadFailed: getCategoryModalText('error.loadFailed'),
        retry: getCategoryModalText('error.retry'),
      },
      states: {
        noMatching: getCategoryModalText('states.noMatching'),
        noCategories: getCategoryModalText('states.noCategories'),
        current: getCategoryModalText('states.current'),
      },
      assign: {
        title: getCategoryModalText('assign.title'),
        selectedCategory: getCategoryModalText('assign.selectedCategory'),
        alreadyCurrent: getCategoryModalText('assign.alreadyCurrent'),
        pleaseSelect: getCategoryModalText('assign.pleaseSelect'),
        assignButton: getCategoryModalText('assign.assignButton'),
      },
      create: {
        title: getCategoryModalText('create.title'),
        subcategoryUnder: getCategoryModalText('create.subcategoryUnder'),
        label: getCategoryModalText('create.label'),
        button: getCategoryModalText('create.button'),
        helperTopLevel: getCategoryModalText('create.helperTopLevel'),
        helperSubcategory: getCategoryModalText('create.helperSubcategory'),
      },
      edit: {
        title: getCategoryModalText('edit.title'),
        currentlyEditing: getCategoryModalText('edit.currentlyEditing'),
        label: getCategoryModalText('edit.label'),
        updateButton: getCategoryModalText('edit.updateButton'),
        deleteButton: getCategoryModalText('edit.deleteButton'),
      },
      deleteDialog: {
        title: getCategoryModalText('deleteDialog.title'),
        description: getCategoryModalText('deleteDialog.description'),
        warning: getCategoryModalText('deleteDialog.warning'),
        cancel: getCategoryModalText('deleteDialog.cancel'),
        delete: getCategoryModalText('deleteDialog.delete'),
      },
      buttons: {
        close: getCategoryModalText('buttons.close'),
      },
      notifications: {
        selectCategory: getCategoryModalText('notifications.selectCategory'),
        categoryUpdated: getCategoryModalText('notifications.categoryUpdated'),
        updateFailed: getCategoryModalText('notifications.updateFailed'),
      },
    }),
    [getCategoryModalText]
  );

  // Memoized completeness drilldown translations - needed by CompletenessDrilldown component
  const getCompletenessDrilldownLabels = useMemo(
    () => ({
      title: getCompletenessDrilldownText('title'),
      description: getCompletenessDrilldownText('description'),
      labels: {
        overallCompleteness: getCompletenessDrilldownText('labels.overallCompleteness'),
        fieldCompleteness: getCompletenessDrilldownText('labels.fieldCompleteness'),
        field: getCompletenessDrilldownText('labels.field'),
        attribute: getCompletenessDrilldownText('labels.attribute'),
        status: getCompletenessDrilldownText('labels.status'),
        weight: getCompletenessDrilldownText('labels.weight'),
        completeFields: getCompletenessDrilldownText('labels.completeFields'),
        missingFields: getCompletenessDrilldownText('labels.missingFields'),
        completeAttributes: getCompletenessDrilldownText('labels.completeAttributes'),
        missingAttributes: getCompletenessDrilldownText('labels.missingAttributes'),
      },
      statuses: {
        complete: getCompletenessDrilldownText('statuses.complete'),
        missing: getCompletenessDrilldownText('statuses.missing'),
      },
      completenessLevels: {
        poor: {
          text: getCompletenessDrilldownText('completenessLevels.poor.text'),
          threshold: getCompletenessDrilldownText('completenessLevels.poor.threshold'),
        },
        fair: {
          text: getCompletenessDrilldownText('completenessLevels.fair.text'),
          threshold: getCompletenessDrilldownText('completenessLevels.fair.threshold'),
        },
        good: {
          text: getCompletenessDrilldownText('completenessLevels.good.text'),
          threshold: getCompletenessDrilldownText('completenessLevels.good.threshold'),
        },
        excellent: {
          text: getCompletenessDrilldownText('completenessLevels.excellent.text'),
          threshold: getCompletenessDrilldownText('completenessLevels.excellent.threshold'),
        },
      },
      weightTooltips: {
        field: {
          high: getCompletenessDrilldownText('weightTooltips.field.high'),
          medium: getCompletenessDrilldownText('weightTooltips.field.medium'),
          low: getCompletenessDrilldownText('weightTooltips.field.low'),
        },
        attribute: {
          high: getCompletenessDrilldownText('weightTooltips.attribute.high'),
          medium: getCompletenessDrilldownText('weightTooltips.attribute.medium'),
          low: getCompletenessDrilldownText('weightTooltips.attribute.low'),
        },
      },
      buttons: {
        close: getCompletenessDrilldownText('buttons.close'),
      },
    }),
    [getCompletenessDrilldownText]
  );

  // Memoized platform sync translations - needed by ProductSyncButtons component
  const getPlatformSyncLabels = useMemo(
    () => ({
      title: getPlatformSyncText('title'),
      loadingPlatforms: getPlatformSyncText('loadingPlatforms'),
      noPlatformsConfigured: getPlatformSyncText('noPlatformsConfigured'),
      syncNow: getPlatformSyncText('syncNow'),
      syncing: getPlatformSyncText('syncing'),
      checkStatus: getPlatformSyncText('checkStatus'),
      syncProduct: getPlatformSyncText('syncProduct'),
      selectPlatformsDescription: getPlatformSyncText('selectPlatformsDescription'),
      selectPlatforms: getPlatformSyncText('selectPlatforms'),
      syncResults: getPlatformSyncText('syncResults'),
      close: getPlatformSyncText('close'),
      syncToPlatforms: getPlatformSyncText('syncToPlatforms'),
      platforms: {
        magento: getPlatformSyncText('platforms.magento'),
        shopify: getPlatformSyncText('platforms.shopify'),
        bigcommerce: getPlatformSyncText('platforms.bigcommerce'),
      },
    }),
    [getPlatformSyncText]
  );

  // Return object with both simple helper functions and memoized objects for compatibility
  return {
    // Translation objects
    t: tSafe,

    // Simple helper functions (used by AssetsTab and other new components)
    getSidebarText,
    getPricingModalText,
    getCategoryModalText,
    getFamilyChangeModalText,
    getTagsModalText,
    getPageText,
    getDescriptionText,
    getTabsText,
    getAddPanelText,
    getAttributesText,
    getAssetsText,
    getProductAssetsText,
    getCompletenessDrilldownText,
    getHistoryText,
    getPriceTabText,
    getPricingFormText,
    getCommentsText,

    // Memoized objects (used by existing components like ProductDetail)
    getPageLabels,
    getSidebarLabels,
    getTabsLabels,
    getDescriptionLabels,
    getTagsModalLabels,
    getAttributesLabels,
    getAssetsLabels,
    getHistoryLabels,

    // Memoized price tab translations - needed by PriceTab component
    getPriceTabLabels,

    // Memoized pricing form translations - needed by PricingForm component
    getPricingFormLabels,

    // Notification helpers with parameters
    getSidebarNotification,
    getPricingModalNotification,
    getCategoryModalNotification,
    getFamilyChangeModalNotification,
    getTagsModalNotification,
    getPageNotification,
    getDescriptionNotification,
    getTabsNotification,
    getAddPanelNotification,
    getAttributesNotification,
    getAssetsNotification,
    getHistoryNotification,
    getPriceTabNotification,
    getPricingFormNotification,
    getCommentsNotification,

    // Current language state
    currentLanguage: i18n.language || 'en',

    // Memoized pricing modal translations - needed by PricingModal component
    getPricingModalLabels,

    // Memoized comment translations
    getCommentsTabLabels,
    getCommentsFormLabels,
    getCommentsItemLabels,
    getCommentsStatsLabels,

    // New helper functions
    getProductComponentsText,
    getLayoutText,
    getUIText,

    // Memoized category modal translations - needed by CategoryModal component
    getCategoryModalLabels,

    // Memoized completeness drilldown translations - needed by CompletenessDrilldown component
    getCompletenessDrilldownLabels,

    // Memoized platform sync translations - needed by ProductSyncButtons component
    getPlatformSyncLabels,

    // Layout translations
    layout: {
      header: {
        search: {
          placeholder: getLayoutText('header.search.placeholder'),
        },
        userMenu: {
          title: getLayoutText('header.userMenu.title'),
          logOut: getLayoutText('header.userMenu.logOut'),
        },
        fallbacks: {
          userName: getLayoutText('header.fallbacks.userName'),
          userEmail: getLayoutText('header.fallbacks.userEmail'),
        },
      },
      sidebar: {
        pinStates: {
          pin: getLayoutText('sidebar.pinStates.pin'),
          unpin: getLayoutText('sidebar.pinStates.unpin'),
          expand: getLayoutText('sidebar.pinStates.expand'),
          collapse: getLayoutText('sidebar.pinStates.collapse'),
        },
      },
    },

    // UI translations
    ui: {
      common: {
        loading: getUIText('common.loading'),
        error: getUIText('common.error'),
        success: getUIText('common.success'),
        cancel: getUIText('common.cancel'),
        save: getUIText('common.save'),
        close: getUIText('common.close'),
        back: getUIText('common.back'),
        next: getUIText('common.next'),
        previous: getUIText('common.previous'),
        retry: getUIText('common.retry'),
        refresh: getUIText('common.refresh'),
      },
      actions: {
        add: getUIText('actions.add'),
        edit: getUIText('actions.edit'),
        delete: getUIText('actions.delete'),
        remove: getUIText('actions.remove'),
        create: getUIText('actions.create'),
        update: getUIText('actions.update'),
        view: getUIText('actions.view'),
        download: getUIText('actions.download'),
        upload: getUIText('actions.upload'),
        export: getUIText('actions.export'),
        import: getUIText('actions.import'),
      },
      states: {
        active: getUIText('states.active'),
        inactive: getUIText('states.inactive'),
        enabled: getUIText('states.enabled'),
        disabled: getUIText('states.disabled'),
        draft: getUIText('states.draft'),
        published: getUIText('states.published'),
        archived: getUIText('states.archived'),
      },
    },
  };
}
